// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const EvidenceCategory = {
    IDENTITY_PROOF: 'identity_proof',
    CORRESPONDENCE: 'correspondence',
    INTERNAL_DATA: 'internal_data',
    LEGAL: 'legal',
    OTHER: 'other',
};
export const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'message/rfc822',
    // Sprint 43.5.2 — platform-generated export formats. These are produced
    // by UnifiedExportService (ZipBuilder for evidence packs / per-case
    // bundles; DOCX/XLSX for reports; JSON for machine-readable outputs).
    // Without these on the global allow-list, the org-policy MIME check in
    // GenericAttachmentService.upload() rejects the server-generated bytes
    // with "File type 'application/zip' is not allowed by the org policy".
    'application/zip',
    'application/x-zip-compressed',
    'application/json',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_TOTAL_ATTACHMENTS = 50;
export const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB
// ────────────────────────────────────────────────────────────────
// Generic attachments (Sprint 41.0.0)
//
// A single shared attachment surface that any module can use without
// coupling to Rights Requests. Backed by `grc_attachments` collection
// and mounted at `/api/v1/attachments`.
// ────────────────────────────────────────────────────────────────
/**
 * Allowlist of owner types that can receive attachments.
 *
 * **Contract:** every value listed here MUST also be registered in
 * `OWNER_TYPE_ALLOWLIST` in `GenericAttachmentService` (backend). A drift
 * test enforces the two lists stay in sync.
 */
export const AttachmentOwnerType = {
    // Sprint 41.0.0 — Governance (G2-G5)
    GOVERNANCE_EVIDENCE: 'governance_evidence',
    PLAYBOOK_EXECUTION_STEP: 'playbook_execution_step',
    OFFBOARDING_DATA_RETURN: 'offboarding_data_return',
    APPROVAL_DECISION: 'approval_decision',
    // Sprint 41.1.0 — Vendor Risk (V2-V3)
    VENDOR_QUESTIONNAIRE_ANSWER: 'vendor_questionnaire_answer',
    VENDOR_ONBOARDING_CASE: 'vendor_onboarding_case',
    // Sprint 41.2.0 E1 — Rights Requests migration off legacy AttachmentService
    RIGHTS_REQUEST_ATTACHMENT: 'rights_request_attachment',
    // Sprint 41.3.0 F0 — Platform-wide AttachmentField adoption
    DATA_MAPPING_EVIDENCE: 'data_mapping_evidence',
    DELETION_STEP_EXECUTION: 'deletion_step_execution',
    INCIDENT_EVIDENCE: 'incident_evidence',
    MONITORING_TASK_INSTANCE: 'monitoring_task_instance',
    DPIA_RECORD: 'dpia_record',
    TRANSFER_ASSESSMENT_RECORD: 'transfer_assessment_record',
    FORM_INSTANCE_RESPONSE: 'form_instance_response',
    CONSENT_WORKFLOW: 'consent_workflow',
    INCIDENT_OPS_EVIDENCE: 'incident_ops_evidence',
    // Sprint 41.4.0 F0.4 — Standalone export persistence (organization-
    // owned, no owner_id). Consumed by Sprint 41.5.0 (Governance final
    // polish: cross-border filing PDFs, board reports, evidence bundles)
    // and Sprint 41.6.0 (Vendor Risk final polish: case evidence packs,
    // incident report PDFs, vendor onboarding bundles). These rows have
    // `owner_id = null` because the owning concept is the org itself,
    // not a single entity in another collection.
    GOVERNANCE_CROSS_BORDER_EXPORT: 'governance_cross_border_export',
    GOVERNANCE_BOARD_REPORT_EXPORT: 'governance_board_report_export',
    GOVERNANCE_EVIDENCE_BUNDLE_EXPORT: 'governance_evidence_bundle_export',
    CASE_EVIDENCE_PACK_EXPORT: 'case_evidence_pack_export',
    // Sprint 58.0.0 (Phase 25 / Sprint A) — PDPL DPIA / OTIA evidence pack
    // ZIPs produced by PdplEvidencePackZipService and routed through
    // UnifiedExportService per R-33. Two variants so storage / retention /
    // metadata can diverge if the regulator differentiates the form types.
    PDPL_EVIDENCE_PACK_DPIA_EXPORT: 'pdpl_evidence_pack_dpia_export',
    PDPL_EVIDENCE_PACK_OTIA_EXPORT: 'pdpl_evidence_pack_otia_export',
    INCIDENT_REPORT_EXPORT: 'incident_report_export',
    VENDOR_ONBOARDING_EXPORT: 'vendor_onboarding_export',
    // Sprint 47.5.3b — Forseti AI-generated artifacts (DOCX outputs from
    // `generate_docx`, redlined DOCX from accept_edit). Org-owned, no
    // single owner_id (the AI surface produces these without a parent
    // record). Consumed by the chat surface in 47.5.4 to surface
    // "View / Download" on the message that triggered the generation.
    FORSETI_ARTIFACT: 'forseti_artifact',
    // Sprint 42.1.0 E4 — Data Mapping PDF exports now persist through
    // the File Component infrastructure instead of raw buffer return.
    DATA_MAPPING_EXPORT: 'data_mapping_export',
    // Sprint 42.1.0 E5 — Classification PDF exports
    CLASSIFICATION_EXPORT: 'classification_export',
    // Sprint 42.2.2 — migrate remaining jsPDF usages to File Component
    INCIDENT_REPORTS_LIST_EXPORT: 'incident_reports_list_export',
    RIGHTS_REQUEST_RESPONSE_EXPORT: 'rights_request_response_export',
    // Sprint 42.2.3 E2 — unified CSV exports through UnifiedExportService
    AUDIT_EVENTS_EXPORT: 'audit_events_export',
    CONSENT_AUDIT_EXPORT: 'consent_audit_export',
    DISCOVERY_REPORT_EXPORT: 'discovery_report_export',
    FORM_FILING_CASE_LINKS_EXPORT: 'form_filing_case_links_export',
    ORG_GROUP_AUDIT_EXPORT: 'org_group_audit_export',
    // Sprint 42.2.3 E3 — access accountability client-side CSVs migrated
    ACCESS_ACCOUNTABILITY_EXPORT: 'access_accountability_export',
    // Consent evidence pack JSON exports through UnifiedExportService
    CONSENT_EVIDENCE_PACK_EXPORT: 'consent_evidence_pack_export',
    // Sprint 42.2.3 E5b — generic CSV pass-through for frontend pages
    // that build CSV in-browser (consent, monitoring, etc.). Lets every
    // page surface its export in FilesBrowserPage without per-module
    // backend wiring.
    CLIENT_CSV_EXPORT: 'client_csv_export',
    // Sprint 41.7.0 R6 — Form output per-category owner types (regulator
    // submissions via FormOutputService). Distinct from the GOVERNANCE_*
    // and INCIDENT_REPORT_EXPORT types which are internal reports/summaries.
    FORM_OUTPUT_DPIA: 'form_output_dpia',
    FORM_OUTPUT_TRANSFER_ASSESSMENT: 'form_output_transfer_assessment',
    FORM_OUTPUT_DOSSIER: 'form_output_dossier',
    FORM_OUTPUT_INCIDENT_REPORT: 'form_output_incident_report',
    FORM_OUTPUT_BREACH_NOTIFICATION: 'form_output_breach_notification',
    FORM_OUTPUT_AUTHORITY_REQUEST: 'form_output_authority_request',
    FORM_OUTPUT_CROSS_BORDER_FILING: 'form_output_cross_border_filing',
    FORM_OUTPUT_OTHER: 'form_output_other',
    // Sprint 41.6.0 V2.6 — Vendor-risk file-based artifacts
    VENDOR_CONTRACT: 'vendor_contract',
    VENDOR_SOC_REPORT: 'vendor_soc_report',
    VENDOR_DPA: 'vendor_dpa',
    VENDOR_NDA: 'vendor_nda',
    VENDOR_TERMINATION_NOTICE: 'vendor_termination_notice',
    VENDOR_AMENDMENT: 'vendor_amendment',
    // Sprint 43.1.0 batch 2 — Rights Request artefacts (E3 Case File PDF,
    // E5 Phụ lục VI Evidence Pack ZIP). Both are standalone, org-owned
    // exports persisted via UnifiedExportService → grc_attachments. The
    // CASE_EVIDENCE_PACK_EXPORT entry above is the legacy stub owner type
    // produced by the now-renamed CaseEvidencePackService; the two new
    // entries below replace it for the comprehensive 13-section PDF and
    // the assembled ZIP. The legacy entry is retained for backwards
    // compatibility with any historical rows already persisted under it.
    RIGHTS_REQUEST_CASE_FILE: 'rights_request_case_file',
    RIGHTS_REQUEST_EVIDENCE_PACK: 'rights_request_evidence_pack',
    // Sprint 43.2.0 batch 2 (E3) — Bulk case export. Standalone org-owned
    // export persisted via UnifiedExportService → grc_attachments. Covers
    // the pdf (aggregated summary), csv (delegated to exportCasesCSV),
    // and zip ("ZIP of ZIPs" of per-case evidence packs + index.json)
    // produced by POST /rights-requests/cases/export/bulk.
    RIGHTS_REQUEST_BULK_EXPORT: 'rights_request_bulk_export',
    // Sprint 43.5.5 — Vendor Risk register exports migrated to
    // UnifiedExportService so they appear in the Files Browser and
    // support multi-format output (CSV/XLSX/JSON).
    VENDOR_RISK_ACCEPTANCE_EXPORT: 'vendor_risk_acceptance_export',
    VENDOR_SUBPROCESSOR_REGISTER_EXPORT: 'vendor_subprocessor_register_export',
    // Sprint 43.6.0 W4 — Vendor Risk Questionnaire exports (R-33).
    // Filled instance exports (PDF/DOCX audit record, CSV/XLSX analytics)
    // and the portfolio-list export (CSV/XLSX) flow through
    // UnifiedExportService the same way risk-acceptance-register exports
    // do. Standalone org-owned; owner_id = null. Template-level exports
    // (blank questionnaire as DOCX for vendor send-out) use the separate
    // VENDOR_QUESTIONNAIRE_TEMPLATE_EXPORT owner type so Files Browser
    // filtering can distinguish filled instances from blank templates.
    VENDOR_QUESTIONNAIRE_EXPORT: 'vendor_questionnaire_export',
    VENDOR_QUESTIONNAIRE_TEMPLATE_EXPORT: 'vendor_questionnaire_template_export',
    // Sprint 43.7.0 Wave 5 — Governance Model per-model exports (CSV /
    // XLSX / JSON / PDF / DOCX) through UnifiedExportService. Standalone
    // org-owned; owner_id references the `_id` of the governance model
    // row when supplied by the route.
    GOVERNANCE_MODEL_EXPORT: 'governance_model_export',
    // Sprint 43.7.2 Wave 3 — R-33 migration for 7 Program Governance
    // list/dashboard pages that previously used `downloadClientExport()`.
    // All flow through UnifiedExportService.generateFromDefinition now
    // (csv/xlsx/pdf/docx) and persist as standalone org-owned exports
    // in the File Component. AuditReadiness exports the latest score +
    // history snapshot; the other six export their filtered list.
    AUDIT_READINESS_EXPORT: 'audit_readiness_export',
    DOCUMENT_CHANGE_LOG_EXPORT: 'document_change_log_export',
    GOVERNANCE_DOCUMENTS_EXPORT: 'governance_documents_export',
    DOCUMENT_ISSUANCE_EXPORT: 'document_issuance_export',
    MATURITY_MODELS_EXPORT: 'maturity_models_export',
    // Sprint 48.2.5 — Forseti maturity walkthrough DOCX deliverable
    // (improvement-roadmap report drawn from a persisted walkthrough draft).
    // Standalone org-owned (owner_id = null); orphan detection N/A. Distinct
    // from `maturity_models_export` (the per-page list export) so the Files
    // Browser can filter Forseti-generated walkthrough roadmaps from generic
    // maturity-model list exports.
    MATURITY_WALKTHROUGH_EXPORT: 'maturity_walkthrough_export',
    // Sprint 48.2.9 — Forseti audit walkthrough DOCX deliverable
    // (per-control walkthrough script drawn from a persisted walkthrough
    // draft). Auditor uses the deliverable as the field-prep document
    // for an audit kickoff.
    AUDIT_WALKTHROUGH_EXPORT: 'audit_walkthrough_export',
    REMEDIATION_PLANS_EXPORT: 'remediation_plans_export',
    REPORTING_PERIODS_EXPORT: 'reporting_periods_export',
    TREND_METRICS_EXPORT: 'trend_metrics_export',
    // Sprint 43.7.2 Wave 3 residual closeout — the initial wave-3 audit
    // missed 4 program-governance pages still using
    // `downloadClientExport()` or `window.print()`. These route their
    // exports through UnifiedExportService like the rest:
    //   ACKNOWLEDGMENT_EXPORT — AcknowledgmentTrackingPage list.
    //   DOC_DRAFTING_EXPORT — DocumentDraftingPage (governance_documents
    //     filtered to draft/in_review/approved statuses).
    //   PROGRAM_HEALTH_EXPORT — ProgramHealthPage KPI snapshot + per-
    //     module trend scorecards aggregated in one report.
    //   RACI_MATRIX_EXPORT — ObligationMappingsPage RACI matrix view
    //     (replaces `window.print()` print dialog).
    // Standalone org-owned; owner_id = null for all four.
    ACKNOWLEDGMENT_EXPORT: 'acknowledgment_export',
    DOC_DRAFTING_EXPORT: 'doc_drafting_export',
    PROGRAM_HEALTH_EXPORT: 'program_health_export',
    RACI_MATRIX_EXPORT: 'raci_matrix_export',
    // Sprint 43.7.3 Wave 3 — R-33 migration for 9 Monitoring list/dashboard
    // pages that previously used `downloadClientExport()`. All route through
    // UnifiedExportService.generateFromDefinition (csv/xlsx/pdf/docx) and
    // persist as standalone org-owned exports in the File Component. The
    // 10th Monitoring page (GovernanceRaciPage) reuses the existing
    // RACI_MATRIX_EXPORT owner type via `/program-governance/raci/export`.
    MONITORING_AUTHORITY_READINESS_EXPORT: 'monitoring_authority_readiness_export',
    MONITORING_CHANGE_REVIEWS_EXPORT: 'monitoring_change_reviews_export',
    MONITORING_FRAMEWORK_LINKS_EXPORT: 'monitoring_framework_links_export',
    MONITORING_KPI_EXPORT: 'monitoring_kpi_export',
    MONITORING_PERIODS_EXPORT: 'monitoring_periods_export',
    MONITORING_PROGRAMS_EXPORT: 'monitoring_programs_export',
    MONITORING_RIC_CASES_EXPORT: 'monitoring_ric_cases_export',
    MONITORING_TASK_TEMPLATES_EXPORT: 'monitoring_task_templates_export',
    MONITORING_TASKS_EXPORT: 'monitoring_tasks_export',
    // Sprint 44.0.1 Wave 3 — R-33 migration for the 3 Consent Properties
    // pages that were still using `downloadClientExport()` (Binding,
    // Registry; ChangeRequestPipeline surfaced via grep scope expansion).
    // All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx) and persist as standalone org-owned exports
    // in the File Component. owner_id = null.
    CONSENT_PROPERTY_BINDING_EXPORT: 'consent_property_binding_export',
    CONSENT_PROPERTY_REGISTRY_EXPORT: 'consent_property_registry_export',
    CONSENT_PROPERTY_CHANGE_REQUESTS_EXPORT: 'consent_property_change_requests_export',
    // Sprint 44.0.2 Wave 3 — R-33 migration for 8 Data Discovery list/dashboard
    // pages. All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx). The 4 other Discovery pages (Dashboard, Cycles,
    // OwnerReview, Lineage) reuse the existing DISCOVERY_REPORT_EXPORT via
    // `/discovery/export/*` from Sprint 42.2.3 E2.
    DISCOVERY_CLASSIFICATION_RULES_EXPORT: 'discovery_classification_rules_export',
    DISCOVERY_DEDUP_RULES_EXPORT: 'discovery_dedup_rules_export',
    DISCOVERY_IMPORT_TEMPLATES_EXPORT: 'discovery_import_templates_export',
    DISCOVERY_CONNECTORS_EXPORT: 'discovery_connectors_export',
    DISCOVERY_SCANS_EXPORT: 'discovery_scans_export',
    DISCOVERY_STAGING_EXPORT: 'discovery_staging_export',
    DISCOVERY_SUGGESTIONS_EXPORT: 'discovery_suggestions_export',
    DISCOVERY_ANALYTICS_EXPORT: 'discovery_analytics_export',
    // Sprint 44.0.3 Waves 2+3 — R-33 migration for all 11 Connectors list
    // pages. Prior to this sprint the `/connectors/export/:artifactType`
    // route returned raw JSON (never a file) and all 11 pages discarded
    // the response. Now each artifact type renders through
    // UnifiedExportService.generateFromDefinition (csv/xlsx/pdf/docx) and
    // persists as a standalone org-owned File Component export
    // (owner_id = null).
    CONNECTOR_AUTOMATION_PROGRAMS_EXPORT: 'connector_automation_programs_export',
    CONNECTOR_SYSTEM_INVENTORY_EXPORT: 'connector_system_inventory_export',
    CONNECTOR_TRACEABILITY_MATRIX_EXPORT: 'connector_traceability_matrix_export',
    CONNECTOR_GAP_REGISTER_EXPORT: 'connector_gap_register_export',
    CONNECTOR_BRDS_EXPORT: 'connector_brds_export',
    CONNECTOR_DECISION_RECORDS_EXPORT: 'connector_decision_records_export',
    CONNECTOR_CONTROL_MAPPINGS_EXPORT: 'connector_control_mappings_export',
    CONNECTOR_INSTANCES_EXPORT: 'connector_instances_export',
    CONNECTOR_DEFINITIONS_EXPORT: 'connector_definitions_export',
    CONNECTOR_EVENT_BUS_EXPORT: 'connector_event_bus_export',
    CONNECTOR_POC_SCORECARDS_EXPORT: 'connector_poc_scorecards_export',
    CONNECTOR_SECURITY_REVIEWS_EXPORT: 'connector_security_reviews_export',
    CONNECTOR_CREDENTIALS_EXPORT: 'connector_credentials_export',
    // Sprint 44.0.4 — Deletion Orchestration QA sweep
    // Backend-rendered exports replacing client-side `downloadClientExport`
    // violations on ExecutionTasksPage, VendorDeletionRequestsPage and
    // ProofChainPage. All flow through UnifiedExportService.
    DELETION_EXECUTION_TASKS_EXPORT: 'deletion_execution_tasks_export',
    DELETION_VENDOR_REQUESTS_EXPORT: 'deletion_vendor_requests_export',
    DELETION_PROOF_CHAIN_EXPORT: 'deletion_proof_chain_export',
    // Sprint 44.0.5 Wave 3 — R-33 migration for 6 Incident Operations list
    // pages. All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx) and persist as standalone org-owned exports in
    // the File Component (owner_id = null). The SLA dashboard + response
    // metrics exports share a backend service method but carry distinct
    // AttachmentOwnerType values so the File Component audit trail
    // distinguishes which page emitted each artefact.
    INCIDENT_OPS_PLAYBOOK_TEMPLATES_EXPORT: 'incident_ops_playbook_templates_export',
    INCIDENT_OPS_REGULATOR_EXPORTS_EXPORT: 'incident_ops_regulator_exports_export',
    INCIDENT_OPS_RESPONSE_METRICS_EXPORT: 'incident_ops_response_metrics_export',
    INCIDENT_OPS_SLA_DASHBOARD_EXPORT: 'incident_ops_sla_dashboard_export',
    INCIDENT_OPS_SLA_DEFINITIONS_EXPORT: 'incident_ops_sla_definitions_export',
    INCIDENT_OPS_VENDOR_COORDINATION_EXPORT: 'incident_ops_vendor_coordination_export',
    // Sprint 44.0.6 Wave 2 — R-33 migration for Access Accountability
    // list/dashboard + entity-detail exports. Upgrades 8 pages that used
    // the 1-format `ACCESS_ACCOUNTABILITY_EXPORT` pass-through to 4-format
    // `generateFromDefinition`, and replaces the `new Blob([JSON...])`
    // direct client-side JSON export on the Evidence Package detail page
    // with a backend multi-format export. Standalone org-owned
    // (owner_id = null); orphan detection not applicable.
    ACCESS_SESSION_EVENTS_EXPORT: 'access_session_events_export',
    ACCESS_ANCHORING_PROOFS_EXPORT: 'access_anchoring_proofs_export',
    ACCESS_JUSTIFICATIONS_EXPORT: 'access_justifications_export',
    ACCESS_INVESTIGATIONS_LIST_EXPORT: 'access_investigations_list_export',
    ACCESS_INVESTIGATION_CASE_EXPORT: 'access_investigation_case_export',
    ACCESS_LOG_LIST_EXPORT: 'access_log_list_export',
    ACCESS_POLICIES_EXPORT: 'access_policies_export',
    ACCESS_REVIEW_CAMPAIGNS_EXPORT: 'access_review_campaigns_export',
    ACCESS_EVIDENCE_PACKAGES_EXPORT: 'access_evidence_packages_export',
    ACCESS_EVIDENCE_PACKAGE_DETAIL_EXPORT: 'access_evidence_package_detail_export',
    // Sprint 44.0.7 Wave 2 — R-33 migration for Organizations + Org Groups
    // list/dashboard pages. All standalone org-owned (owner_id = null);
    // orphan detection N/A. The pre-existing ORG_GROUP_AUDIT_EXPORT from
    // Sprint 42.2.3 E2 remains for the legacy `POST /audit/export` path
    // used programmatically; the new consolidated + per-org list exports
    // below have their own owner types so Files Browser can filter them.
    ORGANIZATIONS_EXPORT: 'organizations_export',
    ORGANIZATION_INTAKE_CONFIGS_EXPORT: 'organization_intake_configs_export',
    ORGANIZATION_DEPARTMENTS_EXPORT: 'organization_departments_export',
    ORGANIZATION_FRAMEWORK_ASSIGNMENTS_EXPORT: 'organization_framework_assignments_export',
    ORGANIZATION_COMPLIANCE_ROLLUP_EXPORT: 'organization_compliance_rollup_export',
    ORG_GROUPS_LIST_EXPORT: 'org_groups_list_export',
    ORG_GROUP_DASHBOARD_EXPORT: 'org_group_dashboard_export',
    ORG_GROUP_REPORTS_EXPORT: 'org_group_reports_export',
    ORG_GROUP_CONSOLIDATED_AUDIT_EXPORT: 'org_group_consolidated_audit_export',
    ORG_GROUP_PER_ORG_AUDIT_EXPORT: 'org_group_per_org_audit_export',
    ORG_GROUP_USERS_EXPORT: 'org_group_users_export',
    ORG_GROUP_SHARED_VENDORS_EXPORT: 'org_group_shared_vendors_export',
    ORG_GROUP_SHARED_FRAMEWORKS_EXPORT: 'org_group_shared_frameworks_export',
    ORG_GROUP_SHARED_FORM_TEMPLATES_EXPORT: 'org_group_shared_form_templates_export',
    ORG_GROUP_MERGED_VENDORS_EXPORT: 'org_group_merged_vendors_export',
    // Sprint 44.0.8 Wave 3 — R-33 migration for 14 Partner Workspace pages.
    // All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx). Standalone org-owned (owner_id = null). The
    // pre-existing `POST /audit/export` path (Sprint 42.0.0 F1) remains for
    // programmatic consumers; the new `GET /audit/export-list` drives the UI.
    PARTNER_ORGS_EXPORT: 'partner_orgs_export',
    PARTNER_ORG_DETAIL_EXPORT: 'partner_org_detail_export',
    PARTNER_DASHBOARD_EXPORT: 'partner_dashboard_export',
    PARTNER_REPORTS_EXPORT: 'partner_reports_export',
    PARTNER_AUDIT_LIST_EXPORT: 'partner_audit_list_export',
    PARTNER_WORKFLOW_TEMPLATES_EXPORT: 'partner_workflow_templates_export',
    PARTNER_WORKFLOW_INSTANCES_EXPORT: 'partner_workflow_instances_export',
    PARTNER_DELIVERABLES_EXPORT: 'partner_deliverables_export',
    PARTNER_DELIVERABLE_TEMPLATES_EXPORT: 'partner_deliverable_templates_export',
    PARTNER_SUPPORT_TICKETS_EXPORT: 'partner_support_tickets_export',
    PARTNER_TICKET_DETAIL_EXPORT: 'partner_ticket_detail_export',
    PARTNER_ESCALATION_DASHBOARD_EXPORT: 'partner_escalation_dashboard_export',
    PARTNER_SERVICE_TIERS_EXPORT: 'partner_service_tiers_export',
    PARTNER_BRAND_PROFILES_EXPORT: 'partner_brand_profiles_export',
    // Sprint 44.0.9 Wave 3 — R-33 migration for 12 Task Management pages.
    // All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx). Standalone org-owned (owner_id = null). The
    // pre-existing `GET /tasks/export` (Sprint 17.0.0, gated by TM_EXPORT)
    // remains for programmatic consumers; the new `GET /tasks/export-list`
    // drives the TaskListPage UI. Sprint 44.0.9 CLOSES the 44.0.x series.
    TASK_LIST_EXPORT: 'task_list_export',
    TASK_BOARD_EXPORT: 'task_board_export',
    TASK_DETAIL_EXPORT: 'task_detail_export',
    MY_TASKS_EXPORT: 'my_tasks_export',
    OVERDUE_TASKS_EXPORT: 'overdue_tasks_export',
    TASK_CALENDAR_EXPORT: 'task_calendar_export',
    WORKFLOW_LIST_EXPORT: 'workflow_list_export',
    WORKFLOW_DETAIL_EXPORT: 'workflow_detail_export',
    WORKFLOW_TEMPLATE_CATALOG_EXPORT: 'workflow_template_catalog_export',
    TASK_CONFLICT_DETECTION_EXPORT: 'task_conflict_detection_export',
    TASK_CONSOLIDATION_EXPORT: 'task_consolidation_export',
    TASK_SECTION_ASSIGNMENTS_EXPORT: 'task_section_assignments_export',
    // Fix Sprint 20260418-0500 Finding 14 — R-33 migration for FormOutputsPage
    // which previously triggered `window.print()` (a forbidden client-side
    // export pattern). The new `GET /forms/instances/:instanceId/print-export`
    // route renders the form instance through UnifiedExportService.
    // generateFromDefinition (PDF only) and persists as a standalone
    // org-owned export in the File Component (owner_id = null).
    FORM_OUTPUT_PRINT_EXPORT: 'form_output_print_export',
    // Follow-up 2 — Cover-letter auto-generation (2026-04-21). Standalone org-
    // owned exports persisted via UnifiedExportService → grc_attachments.
    // Replaces the two form-schema-based manual cover letters
    // (vn_pdpl2025_dpia_cover_letter + vn_mps_submission_cover_letter).
    COVER_LETTER_DPIA_DOSSIER: 'cover_letter_dpia_dossier',
    COVER_LETTER_MPS_SUBMISSION: 'cover_letter_mps_submission',
    // Sprint 58.1.0 (Phase 25 / Sprint B) — Manual sent-proof upload for
    // the PDPL evidence-pack email flow. Operators upload a screenshot or
    // raw .eml after sending the email through their own mail client (the
    // architecture rule LOCKS direct-send to customer-controlled
    // infrastructure — AesirX never sends on the customer's behalf).
    // Owner_id is the `pack_id`, so each proof links 1:N to the assembly
    // doc. Immutable once attached: no DELETE / PATCH on the
    // grc_pdpl_email_sent_proofs collection.
    PDPL_EMAIL_SEND_PROOF_2026: 'pdpl_email_send_proof_2026',
    // Sprint 58.3.0 (Phase 25 / Sprint D) — Direct-send chain-of-custody
    // artefacts. Owner_id is the send_id from grc_pdpl_email_send_records.
    // PDPL_AUTHORITY_RESPONSE_EVIDENCE_2026 carries the authority's reply
    // (`.eml`) or scan; PDPL_HARD_COPY_RECEIPT_2026 carries postal receipt
    // evidence (courier slip, signed acknowledgement). Both append-only
    // from the service's perspective — once attached, they are never
    // edited (a re-upload creates a NEW attachment row).
    PDPL_AUTHORITY_RESPONSE_EVIDENCE_2026: 'pdpl_authority_response_evidence_2026',
    PDPL_HARD_COPY_RECEIPT_2026: 'pdpl_hard_copy_receipt_2026',
};
//# sourceMappingURL=attachment.js.map